import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Bar, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { ArrowLeft, Trophy, TrendingDown, TrendingUp, Fuel, Truck, Package, Target, CalendarDays, MessageCircle } from 'lucide-react';
import CommentsPopover from '@/components/admin/fleet/CommentsPopover';
import EditDayDialog from '@/components/admin/fleet/EditDayDialog';
import EditedNote from '@/components/admin/fleet/EditedNote';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => Number(n || 0).toLocaleString('en-ZA');
const fmtR = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PERIODS = [
  { key: 'week',   label: 'This Week' },
  { key: 'month',  label: 'This Month' },
  { key: 'custom', label: 'Custom' },
];

export default function FleetTripsDriverDetail() {
  const { driverId } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();

  const [period, setPeriod] = useState(search.get('period') || 'month');
  const [dateFrom, setDateFrom] = useState(search.get('from') || '');
  const [dateTo, setDateTo] = useState(search.get('to') || '');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const params = { driver_id: driverId, period };
      if (period === 'custom') {
        if (!dateFrom || !dateTo) { setLoading(false); return; }
        params.date_from = dateFrom; params.date_to = dateTo;
      }
      const r = await axios.get(`${API}/driver-trips/driver-detail`, { params });
      setData(r.data);
      // reflect state in the URL so the back-link keeps period
      const q = { period };
      if (period === 'custom') { q.from = dateFrom; q.to = dateTo; }
      setSearch(q, { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [driverId, period, dateFrom, dateTo, setSearch]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const rangeLabel = useMemo(() => {
    if (!data?.filters) return '';
    const { date_from, date_to } = data.filters;
    return date_from === date_to ? date_from : `${date_from} → ${date_to}`;
  }, [data]);

  const backHref = useMemo(() => {
    const q = new URLSearchParams();
    q.set('period', period);
    if (period === 'custom') { q.set('from', dateFrom); q.set('to', dateTo); }
    return `/admin/fleet-trips?${q.toString()}`;
  }, [period, dateFrom, dateTo]);

  const summary = data?.summary || null;
  const days = data?.days || [];
  const target = data?.target?.daily_stops_target || 80;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <Link to={backHref} className="text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-1" data-testid="back-to-collective">
              <ArrowLeft className="h-3 w-3" /> Back to Collective View
            </Link>
            <h1 className="text-2xl font-bold mt-1 flex items-center gap-2">
              <Truck className="h-6 w-6 text-indigo-600" /> {data?.driver?.name || '—'}
            </h1>
            <div className="text-sm text-muted-foreground">
              {(data?.driver?.role || '').replace('_', ' ')} · Vehicle {data?.driver?.vehicle
                ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-200 font-mono text-xs">🚚 {data.driver.vehicle}</span>
                : <span className="text-slate-400">unassigned</span>}
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPeriod(p.key)}
                  className={`px-3 py-1.5 text-xs rounded ${period === p.key ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  data-testid={`detail-period-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {period === 'custom' && (
              <>
                <div>
                  <Label className="text-[11px]">From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-40" data-testid="detail-from" />
                </div>
                <div>
                  <Label className="text-[11px]">To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-40" data-testid="detail-to" />
                </div>
              </>
            )}
            <div className="ml-auto text-right">
              <div className="text-[11px] text-muted-foreground">Range</div>
              <div className="text-xs font-mono">{rangeLabel || '—'}</div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading…</div>
        ) : !summary ? (
          <div className="text-center py-8 text-muted-foreground text-sm">No data.</div>
        ) : (
          <>
            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="detail-summary">
              <Tile label="Days Worked" value={summary.days_worked} icon={<CalendarDays className="h-4 w-4 text-slate-500" />} />
              <Tile label="Total Stops" value={fmt(summary.total_stops)} icon={<Target className="h-4 w-4 text-emerald-600" />} />
              <Tile label="Kilometres" value={fmt(summary.kilometres)} icon={<Truck className="h-4 w-4 text-blue-600" />} />
              <Tile label="Avg Stops/Day" value={summary.avg_stops_per_day} icon={<Package className="h-4 w-4 text-indigo-600" />} />
              <Tile label="Fuel Spend" value={fmtR(summary.fuel_total)} icon={<Fuel className="h-4 w-4 text-orange-600" />} />
              <Tile label="Performance" value={`${summary.performance_pct}%`}
                    icon={summary.target_met ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-rose-600" />}
                    highlight={summary.target_met ? 'emerald' : 'amber'} />
            </div>

            {/* Best / worst / weekly */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {summary.best_day && (
                <Card className="border-emerald-200 bg-emerald-50/50" data-testid="best-day-card">
                  <CardContent className="p-4">
                    <div className="text-xs uppercase font-semibold text-emerald-800 flex items-center gap-1"><Trophy className="h-3 w-3" /> Best Day</div>
                    <div className="text-lg font-bold text-emerald-900 mt-1">{summary.best_day.date}</div>
                    <div className="text-xs text-emerald-800">{fmt(summary.best_day.stops)} stops · {summary.best_day.performance_pct}%</div>
                  </CardContent>
                </Card>
              )}
              {summary.worst_day && (
                <Card className="border-rose-200 bg-rose-50/50" data-testid="worst-day-card">
                  <CardContent className="p-4">
                    <div className="text-xs uppercase font-semibold text-rose-800 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Lowest Day</div>
                    <div className="text-lg font-bold text-rose-900 mt-1">{summary.worst_day.date}</div>
                    <div className="text-xs text-rose-800">{fmt(summary.worst_day.stops)} stops · {summary.worst_day.performance_pct}%</div>
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardContent className="p-4">
                  <div className="text-xs uppercase font-semibold text-slate-600 flex items-center gap-1"><Target className="h-3 w-3" /> Target</div>
                  <div className="text-lg font-bold text-slate-800 mt-1">{target} stops/day</div>
                  <div className="text-xs text-slate-600">Fuel eff: {summary.fuel_efficiency_km_per_r || 0} km/R</div>
                </CardContent>
              </Card>
            </div>

            {/* Daily trend chart */}
            <Card data-testid="daily-trend-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Daily Performance Trend</CardTitle>
                <CardDescription className="text-xs">Bars = stops that day. Dashed line = daily target.</CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={data.trend} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="stops" name="Stops" fill="#4f46e5" radius={[3, 3, 0, 0]} />
                      <ReferenceLine y={target} stroke="#dc2626" strokeDasharray="4 4" label={{ value: `Target ${target}`, position: 'right', fontSize: 10, fill: '#dc2626' }} />
                      <Line type="monotone" dataKey="target" stroke="transparent" name="Target" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Weekly averages (month view) */}
            {summary.weekly_averages?.length > 0 && (
              <Card data-testid="weekly-averages-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Weekly Averages</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-slate-700 text-xs">
                      <tr>
                        <th className="text-left px-3 py-2">Week #</th>
                        <th className="text-center px-3 py-2">Days</th>
                        <th className="text-center px-3 py-2">Stops</th>
                        <th className="text-center px-3 py-2">KM</th>
                        <th className="text-center px-3 py-2">Avg Stops/Day</th>
                        <th className="text-right px-3 py-2">Perf %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.weekly_averages.map((w) => (
                        <tr key={w.week} className="border-t">
                          <td className="px-3 py-2">Week {w.week}</td>
                          <td className="text-center px-3 py-2">{w.days}</td>
                          <td className="text-center px-3 py-2 font-semibold">{fmt(w.stops)}</td>
                          <td className="text-center px-3 py-2">{fmt(w.km)}</td>
                          <td className="text-center px-3 py-2">{w.avg_stops}</td>
                          <td className="text-right px-3 py-2">
                            <Badge className={w.perf_pct >= 100 ? 'bg-emerald-600' : 'bg-amber-500'}>{w.perf_pct}%</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Day-by-day breakdown */}
            <Card data-testid="daily-breakdown-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Day-by-Day Breakdown</CardTitle>
                <CardDescription className="text-xs">Click 💬 to add or view notes for that day.</CardDescription>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {days.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground text-sm">No trip entries in this range.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-slate-700 text-xs">
                      <tr>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Vehicle</th>
                        <th className="text-center px-3 py-2">Trips</th>
                        <th className="text-center px-3 py-2">KM</th>
                        <th className="text-center px-3 py-2 text-emerald-700">Delivered</th>
                        <th className="text-center px-3 py-2 text-indigo-700">Collected</th>
                        <th className="text-right px-3 py-2">Stops</th>
                        <th className="text-center px-3 py-2 text-orange-700">Fuel</th>
                        <th className="text-right px-3 py-2">Perf %</th>
                        <th className="text-center px-3 py-2">Edit / Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {days.map((r) => (
                        <tr key={r.trip_id} className="border-t" data-testid={`day-row-${r.date}`}>
                          <td className="px-3 py-2 font-mono">{r.date}<EditedNote history={r.edit_history} testId={`edited-note-${r.date}`} /></td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {r.vehicle
                              ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">🚚 {r.vehicle}</span>
                              : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="text-center px-3 py-2">{r.trips}</td>
                          <td className="text-center px-3 py-2">{fmt(r.kilometres)}</td>
                          <td className="text-center px-3 py-2 text-emerald-700 font-semibold">{fmt(r.delivered)}</td>
                          <td className="text-center px-3 py-2 text-indigo-700 font-semibold">{fmt(r.collections)}</td>
                          <td className="text-right px-3 py-2 font-bold">{fmt(r.stops)}</td>
                          <td className="text-center px-3 py-2 text-orange-700">{fmtR(r.fuel)}</td>
                          <td className="text-right px-3 py-2">
                            <Badge className={r.target_met ? 'bg-emerald-600' : 'bg-amber-500'}>{r.performance_pct}%</Badge>
                          </td>
                          <td className="text-center px-3 py-2">
                            <div className="inline-flex items-center gap-1">
                              <EditDayDialog driverId={driverId} row={r} onSaved={fetchDetail} />
                              <CommentsPopover
                                tripId={r.trip_id}
                                comments={r.comments}
                                onChange={fetchDetail}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function Tile({ label, value, icon, highlight }) {
  const cls = highlight === 'emerald' ? 'border-emerald-300 bg-emerald-50/40'
            : highlight === 'amber'   ? 'border-amber-300 bg-amber-50/40'
            : '';
  return (
    <Card className={cls}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">{icon}{label}</div>
        <div className="text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
